import { useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import styled from 'styled-components';
import { Box, Tooltip, Typography } from '@mui/material';
import Chip from '../Chip';
import { Flex, FlexSpace } from '../Flex';
import OverflowTooltip from '../OverflowTooltip';

const GAP_PX = 8;
const COUNTER_RESERVED_WIDTH = 40;

const Wrapper = styled(Box)`
  width: 100%;
  min-width: 0;
  position: relative;
  overflow: hidden;
`;

const VisibleRow = styled(Flex)<{ $forceShrink: boolean }>`
  gap: ${GAP_PX}px;
  align-items: center;
  min-width: 0;
  flex-wrap: nowrap;

  .MuiChip-root {
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ $forceShrink }) =>
      $forceShrink
        ? `
      flex-shrink: 1;
      min-width: 0;
      max-width: 100%;
    `
        : `
      flex-shrink: 0;
    `}
  }
`;

const MeasureRow = styled(Flex)`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  top: 0;
  left: 0;
  gap: ${GAP_PX}px;
  align-items: center;
  white-space: nowrap;
`;

const CounterWrapper = styled(Flex)`
  color: ${({ theme }) => theme.palette.common.black};
  font-weight: 500;
  margin-right: 8px;
  flex-shrink: 0;
`;

const ChipCounterList = ({ items = [] }: { items?: string[] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(1);
  const [forceShrink, setForceShrink] = useState<boolean>(false);
  const [shouldMeasure, setShouldMeasure] = useState<boolean>(false);

  const itemsKey = items.join('\u0000');

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || items.length === 0) return undefined;

    if (!shouldMeasure) {
      if (container.clientWidth > 0) setShouldMeasure(true);
      return undefined;
    }

    const measure = measureRef.current;
    if (!measure) return undefined;

    const computeVisible = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth === 0) return;

      const chipNodes = Array.from(measure.children) as HTMLElement[];
      if (chipNodes.length === 0) {
        setVisibleCount(0);
        setForceShrink(false);
        return;
      }

      const chipWidths = chipNodes.map((n) => n.offsetWidth);
      const allChipsWidth = chipWidths.reduce(
        (acc, w, i) => acc + w + (i > 0 ? GAP_PX : 0),
        0,
      );

      if (allChipsWidth <= containerWidth) {
        setVisibleCount(chipNodes.length);
        setForceShrink(false);
        return;
      }

      const budget = Math.max(
        0,
        containerWidth - COUNTER_RESERVED_WIDTH - GAP_PX,
      );
      let used = 0;
      let count = 0;
      for (let i = 0; i < chipWidths.length; i += 1) {
        const next = chipWidths[i] + (i > 0 ? GAP_PX : 0);
        if (used + next > budget) break;
        used += next;
        count += 1;
      }

      setVisibleCount(Math.max(1, count));
      setForceShrink(count === 0);
    };

    computeVisible();

    const observer = new ResizeObserver(() => {
      flushSync(computeVisible);
    });
    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldMeasure, itemsKey]);

  if (items.length === 0) return null;

  const safeVisibleCount = Math.min(visibleCount, items.length);
  const visibleItems = items.slice(0, safeVisibleCount);
  const hiddenItems = items.slice(safeVisibleCount);

  return (
    <Wrapper ref={containerRef}>
      {shouldMeasure && (
        <MeasureRow ref={measureRef} aria-hidden>
          {items.map((t, i) => (
            <Chip key={`measure-${t}-${i}`} label={t} />
          ))}
        </MeasureRow>
      )}

      <VisibleRow $forceShrink={forceShrink}>
        {visibleItems.map((t, i) => (
          <Chip
            key={`${t}-${i}`}
            label={<OverflowTooltip>{t}</OverflowTooltip>}
          />
        ))}
        {hiddenItems.length > 0 && (
          <CounterWrapper>
            <Tooltip
              title={
                <FlexSpace sx={{ gap: 1 }}>
                  {hiddenItems.map((t, index) => (
                    <div key={`${t}-${index}`}>{`${t}${
                      index < hiddenItems.length - 1 ? ', ' : ''
                    }`}</div>
                  ))}
                </FlexSpace>
              }
            >
              <Typography>{`+${hiddenItems.length}`}</Typography>
            </Tooltip>
          </CounterWrapper>
        )}
      </VisibleRow>
    </Wrapper>
  );
};

export default ChipCounterList;
